/**
 * UI-8e Task 6 — AddOverrideDialog.
 *
 * Centred modal launched from the FlagAccordionItem "+ Add tenant override"
 * button. The operator picks an active tenant + enabled toggle + rollout
 * percent, and the dialog hands the result back to the parent via
 * `onSubmit(tenantId, { enabled, rolloutPct })`. The parent owns the
 * actual PUT call (so it can show a per-row toast) and the refetch.
 *
 * Tenants are sourced from `useTenants({ status: 'active' })` — suspended
 * tenants are hidden so an operator can't accidentally pin a flag to a
 * dormant tenant. The dropdown stays in placeholder state ("Tenant") until
 * a choice is made; Save is disabled while `tenantId === null`.
 *
 * State resets when the dialog closes — re-opening starts with a fresh
 * picker, mirroring ScheduleRotationDialog.
 *
 * Why a Dialog (not a Drawer)? An override is a single-step commit —
 * centred modal matches BulkTagDialog / DncImportDialog elsewhere in the
 * admin app and keeps muscle memory consistent.
 */
import { useEffect, useState, type JSX } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { SelectButton } from 'primereact/selectbutton'
import { Slider } from 'primereact/slider'

import { useTenants } from '../../hooks/useTenants.js'
import type { FlagValue } from '../../hooks/useFeatureFlags.js'

export interface AddOverrideDialogProps {
  open: boolean
  flagKey: string
  onClose: () => void
  onSubmit: (tenantId: string, payload: FlagValue) => Promise<void>
}

const ENABLED_OPTIONS = [
  { label: 'OFF', value: false },
  { label: 'ON', value: true },
]

export function AddOverrideDialog({
  open,
  flagKey,
  onClose,
  onSubmit,
}: AddOverrideDialogProps): JSX.Element {
  const { items: tenants } = useTenants({ status: 'active' })

  const [tenantId, setTenantId] = useState<string | null>(null)
  const [enabled, setEnabled] = useState(false)
  const [rolloutPct, setRolloutPct] = useState(0)
  const [saving, setSaving] = useState(false)

  // Reset picker state whenever the dialog closes / re-opens.
  useEffect(() => {
    if (!open) {
      setTenantId(null)
      setEnabled(false)
      setRolloutPct(0)
    }
  }, [open])

  const tenantOptions = tenants.map((t) => ({ label: t.slug, value: t.id }))
  const canSubmit = tenantId !== null && !saving

  async function handleSubmit(): Promise<void> {
    if (!tenantId) return
    setSaving(true)
    try {
      await onSubmit(tenantId, { enabled, rolloutPct })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      visible={open}
      onHide={onClose}
      header={`Override ${flagKey} for a tenant`}
      modal
      style={{ width: '32rem' }}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="add-override-tenant"
            className="text-sm font-medium"
          >
            Tenant
          </label>
          <Dropdown
            inputId="add-override-tenant"
            value={tenantId}
            options={tenantOptions}
            onChange={(e) => {
              setTenantId(
                typeof e.value === 'string' || e.value === null
                  ? e.value
                  : null,
              )
            }}
            filter
            placeholder="Tenant"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label
            className="text-sm font-medium"
            id="add-override-enabled-label"
          >
            Enabled
          </label>
          <SelectButton
            value={enabled}
            options={ENABLED_OPTIONS}
            onChange={(e) => {
              if (typeof e.value === 'boolean') setEnabled(e.value)
            }}
            aria-labelledby="add-override-enabled-label"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Rollout</label>
          <Slider
            value={rolloutPct}
            onChange={(e) => {
              if (typeof e.value === 'number') setRolloutPct(e.value)
            }}
            min={0}
            max={100}
          />
          <span className="text-xs text-gray-500">{rolloutPct}%</span>
        </div>

        <div className="flex justify-end gap-2">
          <Button label="Cancel" text severity="secondary" onClick={onClose} />
          <Button
            label="Save override"
            disabled={!canSubmit}
            onClick={() => {
              void handleSubmit()
            }}
          />
        </div>
      </div>
    </Dialog>
  )
}

export default AddOverrideDialog
